import { z } from 'zod';
import { auctionContextSchema, buyerPreferencesSchema, cardConditionSchema, cardIdentitySchema, frameMetricsSchema, marketSaleSchema, moneySchema, partialCardIdentitySchema, } from './domain-schemas.js';
/** Ordered pipeline stages. Clients render progressively as these arrive. */
export const SCAN_STAGES = [
    'accepted',
    'quality_checked',
    'identified',
    'comps_retrieved',
    'valued',
    'recommended',
    'complete',
    'failed',
];
export const scanStageSchema = z.enum(SCAN_STAGES);
export const scanSourceSchema = z.enum(['chrome_extension', 'ios_app', 'web_app', 'api']);
export const CAPTURE_PROTOCOL_VERSION = 1;
export const createScanRequestSchema = z.object({
    /**
     * Required so pre-manual-only clients fail closed instead of silently
     * bypassing a newer capture policy.
     */
    clientProtocolVersion: z.literal(CAPTURE_PROTOCOL_VERSION),
    captureTrigger: z.enum(['manual', 'automatic']),
    /** Base64 (no data-URL prefix) JPEG or PNG. */
    imageBase64: z.string().min(64).max(12_000_000),
    imageMimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    source: scanSourceSchema,
    /** Metrics computed on-device so the server can skip work it does not need to redo. */
    clientMetrics: frameMetricsSchema.nullable().optional(),
    auction: auctionContextSchema.nullable().optional(),
    preferences: buyerPreferencesSchema.partial().nullable().optional(),
    /** Client-generated idempotency key; repeat submissions return the original scan. */
    idempotencyKey: z.string().min(8).max(80).optional(),
    /** Page or stream the capture came from, for support and abuse handling. */
    captureContext: z
        .object({
        pageUrl: z.string().max(2000).nullable(),
        platform: z.string().max(60).nullable(),
        streamTitle: z.string().max(300).nullable(),
    })
        .nullable()
        .optional(),
});
export const qualityIssueSchema = z.object({
    code: z.string(),
    message: z.string(),
    severity: z.enum(['reject', 'warn']),
    measured: z.number().nullable(),
    threshold: z.number().nullable(),
    hint: z.string(),
});
export const identityCandidateSchema = z.object({
    identity: cardIdentitySchema,
    confidence: z.number().min(0).max(1),
    catalogId: z.string().nullable(),
    evidence: z.record(z.string(), z.enum(['observed', 'inferred', 'missing', 'user_corrected'])),
    rationale: z.array(z.string()),
});
export const identificationResultSchema = z.object({
    abstained: z.boolean(),
    abstentionReason: z.string().nullable(),
    best: identityCandidateSchema.nullable(),
    alternates: z.array(identityCandidateSchema),
    condition: cardConditionSchema,
    /** Which providers ran, what they cost and how long they took. */
    providerRuns: z.array(z.object({
        provider: z.string(),
        status: z.enum(['ok', 'timeout', 'error', 'skipped', 'abstained']),
        latencyMs: z.number().nonnegative(),
        costMicros: z.number().nonnegative().nullable(),
        error: z.string().nullable(),
    })),
    conflictingProviders: z.boolean(),
});
export const compEvaluationSchema = z.object({
    sale: marketSaleSchema,
    matchScore: z.number(),
    coverage: z.number(),
    ageDays: z.number(),
    weight: z.number(),
    adjustedTotal: moneySchema.nullable(),
    gradeAdjustmentFactor: z.number().nullable(),
    gradeAdjustmentNote: z.string().nullable(),
    included: z.boolean(),
    exclusionReason: z.string().nullable(),
    notes: z.array(z.string()),
});
export const valuationSchema = z.object({
    currency: z.string(),
    asOf: z.string(),
    candidateCount: z.number().int(),
    usedCount: z.number().int(),
    lastSale: z.object({ total: moneySchema, soldAt: z.string(), saleId: z.string() }).nullable(),
    median: moneySchema.nullable(),
    trimmedMean: moneySchema.nullable(),
    mean: moneySchema.nullable(),
    weightedMean: moneySchema.nullable(),
    range: z
        .object({
        min: moneySchema,
        p10: moneySchema,
        p25: moneySchema,
        p75: moneySchema,
        p90: moneySchema,
        max: moneySchema,
    })
        .nullable(),
    fairValue: moneySchema.nullable(),
    fairValueBasis: z.string(),
    trend: z.object({
        changePer30Days: z.number(),
        direction: z.enum(['up', 'down', 'flat']),
        slopePerDay: z.number(),
        method: z.string(),
        sampleSize: z.number().int(),
    }),
    liquidity: z.object({
        salesLast30Days: z.number().int(),
        salesLast90Days: z.number().int(),
        salesPerWeek: z.number(),
        score: z.number(),
        label: z.enum(['illiquid', 'thin', 'moderate', 'liquid']),
    }),
    volatility: z.object({
        coefficientOfVariation: z.number().nullable(),
        relativeMad: z.number().nullable(),
        score: z.number(),
        label: z.enum(['stable', 'normal', 'volatile', 'erratic']),
    }),
    freshness: z.object({
        newestAgeDays: z.number().nullable(),
        medianAgeDays: z.number().nullable(),
        score: z.number(),
        label: z.enum(['fresh', 'recent', 'stale', 'very_stale']),
    }),
    confidence: z.number().min(0).max(1),
    gradeAdjustedShare: z.number(),
    comps: z.array(compEvaluationSchema),
    dataSources: z.array(z.object({
        provider: z.string(),
        licence: z.string(),
        count: z.number().int(),
        displayIndividualRecords: z.boolean(),
        oldestFetchedAt: z.string(),
    })),
    warnings: z.array(z.string()),
});
export const recommendationSchema = z.object({
    verdict: z.enum(['buy', 'fair', 'avoid', 'awaiting_price', 'insufficient_data']),
    confidence: z.number().min(0).max(1),
    maxBid: moneySchema.nullable(),
    maxAllIn: moneySchema.nullable(),
    fairValue: moneySchema.nullable(),
    currentBid: moneySchema.nullable(),
    currentAllIn: moneySchema.nullable(),
    headroom: moneySchema.nullable(),
    headroomPct: z.number().nullable(),
    requiredDiscount: z.number(),
    evidenceScore: z.number(),
    factors: z.array(z.object({
        key: z.string(),
        label: z.string(),
        impact: z.enum(['positive', 'negative', 'neutral']),
        weight: z.number(),
        detail: z.string(),
    })),
    missingInformation: z.array(z.string()),
    warnings: z.array(z.string()),
    disclaimers: z.array(z.string()),
    calculation: z.array(z.object({ step: z.string(), expression: z.string(), value: z.string() })),
    summary: z.string(),
});
export const scanTimingsSchema = z.object({
    receivedAt: z.string(),
    acknowledgedMs: z.number().nonnegative(),
    qualityMs: z.number().nonnegative().nullable(),
    identificationMs: z.number().nonnegative().nullable(),
    compsMs: z.number().nonnegative().nullable(),
    valuationMs: z.number().nonnegative().nullable(),
    recommendationMs: z.number().nonnegative().nullable(),
    totalMs: z.number().nonnegative().nullable(),
    compsCacheHit: z.boolean().nullable(),
});
export const scanSchema = z.object({
    id: z.string(),
    userId: z.string(),
    source: scanSourceSchema,
    stage: scanStageSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    saved: z.boolean(),
    quality: z
        .object({
        usable: z.boolean(),
        score: z.number(),
        issues: z.array(qualityIssueSchema),
    })
        .nullable(),
    identification: identificationResultSchema.nullable(),
    valuation: valuationSchema.nullable(),
    recommendation: recommendationSchema.nullable(),
    auction: auctionContextSchema.nullable(),
    timings: scanTimingsSchema,
    error: z.object({ code: z.string(), message: z.string() }).nullable(),
    imageRetention: z.object({
        stored: z.boolean(),
        deleteAfter: z.string().nullable(),
        policy: z.string(),
    }),
});
/** Server-sent event payload for progressive results. */
export const scanEventSchema = z.object({
    scanId: z.string(),
    stage: scanStageSchema,
    at: z.string(),
    elapsedMs: z.number().nonnegative(),
    scan: scanSchema,
});
export const scanFeedbackRequestSchema = z.object({
    kind: z.enum(['wrong_card', 'wrong_grade', 'wrong_comps', 'bad_image', 'other', 'confirmed_correct']),
    correctedIdentity: partialCardIdentitySchema.nullable().optional(),
    correctedCondition: cardConditionSchema.nullable().optional(),
    note: z.string().max(1000).nullable().optional(),
});
export const valuationRequestSchema = z.object({
    identity: partialCardIdentitySchema,
    condition: cardConditionSchema,
    currency: z.string().length(3).default('USD'),
    auction: auctionContextSchema.nullable().optional(),
    preferences: buyerPreferencesSchema.partial().nullable().optional(),
    forceRefresh: z.boolean().optional(),
});
export const valuationResponseSchema = z.object({
    identity: cardIdentitySchema,
    condition: cardConditionSchema,
    fingerprint: z.string(),
    valuation: valuationSchema,
    recommendation: recommendationSchema,
    cacheHit: z.boolean(),
});
export const watchlistItemSchema = z.object({
    id: z.string(),
    identity: cardIdentitySchema,
    condition: cardConditionSchema,
    fingerprint: z.string(),
    targetPrice: moneySchema.nullable(),
    note: z.string().nullable(),
    createdAt: z.string(),
    lastValuation: valuationSchema.nullable(),
});
export const createWatchlistItemRequestSchema = z.object({
    identity: partialCardIdentitySchema,
    condition: cardConditionSchema,
    targetPrice: moneySchema.nullable().optional(),
    note: z.string().max(500).nullable().optional(),
});
export const usageSchema = z.object({
    periodStart: z.string(),
    periodEnd: z.string(),
    scansUsed: z.number().int(),
    scansIncluded: z.number().int().nullable(),
    scansRemaining: z.number().int().nullable(),
    overageScans: z.number().int(),
    hardLimitReached: z.boolean(),
    /** Daily limits matter more than monthly ones: usage arrives in show-shaped bursts. */
    scansUsedToday: z.number().int(),
    scansPerDay: z.number().int().nullable(),
    scansRemainingToday: z.number().int().nullable(),
    dailyLimitReached: z.boolean(),
    /** True when usage exceeds 90 % of the daily cap. Clients should show a warning. */
    approachingDailyLimit: z.boolean(),
    /** True when the user is in the grace buffer beyond the daily cap (paid plans only). */
    dailyGracePeriod: z.boolean(),
    /** Scans remaining in the grace buffer, or null if grace does not apply. */
    dailyGraceRemaining: z.number().int().nullable(),
});
export const entitlementSchema = z.object({
    planId: z.string(),
    planName: z.string(),
    status: z.enum(['trialing', 'active', 'grace_period', 'past_due', 'canceled', 'expired', 'refunded', 'none']),
    source: z.enum(['stripe', 'apple_app_store', 'google_play', 'manual', 'none']),
    currentPeriodEnd: z.string().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    features: z.object({
        scansPerMonth: z.number().int().nullable(),
        scansPerDay: z.number().int().nullable(),
        liveOverlay: z.boolean(),
        compsHistoryDays: z.number().int(),
        watchlistLimit: z.number().int().nullable(),
        csvExport: z.boolean(),
        prioritySpeed: z.boolean(),
        seats: z.number().int(),
    }),
    updatedAt: z.string(),
});
export const authUserSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    displayName: z.string().nullable(),
    createdAt: z.string(),
    preferences: buyerPreferencesSchema,
    role: z.enum(['user', 'admin']),
});
export const registerRequestSchema = z.object({
    email: z.string().email().max(200),
    password: z.string().min(10).max(200),
    displayName: z.string().max(80).optional(),
});
export const loginRequestSchema = z.object({
    email: z.string().email().max(200),
    password: z.string().min(1).max(200),
});
export const passwordResetRequestSchema = z.object({
    email: z.string().email().max(200),
});
export const passwordResetConfirmSchema = z.object({
    token: z.string().min(20).max(200),
    // Same floor as registration: a reset must not be a way to weaken a password.
    password: z.string().min(10).max(200),
});
export const authResponseSchema = z.object({
    user: authUserSchema,
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresAt: z.string(),
});
export const providerHealthSchema = z.object({
    name: z.string(),
    kind: z.enum(['identification', 'market_data', 'billing', 'storage']),
    status: z.enum(['healthy', 'degraded', 'down', 'disabled']),
    detail: z.string().nullable(),
    lastCheckedAt: z.string(),
    p50LatencyMs: z.number().nullable(),
    p95LatencyMs: z.number().nullable(),
    errorRate: z.number().nullable(),
});
export const clientConfigSchema = z.object({
    supportedPlatforms: z.array(z.object({
        id: z.string(),
        name: z.string(),
        hostPatterns: z.array(z.string()),
        /** Whether in-page frame capture is permitted and technically possible. */
        captureMode: z.enum(['canvas', 'manual_only', 'unsupported']),
        /**
         * Whether the user may turn automatic capture on for this platform.
         *
         * `forbidden` is a policy decision, not a capability one: the sampling
         * code exists and would work, but the platform's terms rule it out, so
         * the client must not render a control that could enable it. Separating
         * this from `captureMode` keeps "we cannot" distinct from "we will not".
         */
        autoCapture: z.enum(['forbidden', 'opt_in_beta']),
        notes: z.string(),
    })),
    qualityThresholds: z.object({
        liveStream: z.record(z.string(), z.number()),
        camera: z.record(z.string(), z.number()),
    }),
    sampling: z.object({
        framesPerSecond: z.number(),
        minMsBetweenUploads: z.number().int(),
        duplicateHammingDistance: z.number().int(),
    }),
    disclaimers: z.array(z.string()),
    version: z.string(),
});
export const errorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
        requestId: z.string().optional(),
    }),
});
export const paginationSchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    cursor: z.string().optional(),
});
//# sourceMappingURL=api-schemas.js.map